/** @jsxImportSource @emotion/react */
import { motion, useAnimate } from 'framer-motion';
import {
  PropsWithChildren,
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo,
  useId,
} from 'react';
import useMeasure from 'react-use-measure';
import useStyle from './style';
import { SerializedStyles, css } from '@emotion/react';
import { Arrow } from '../../Icons';

const InfiniteSlideShow: React.FC<{
  slides: any[];
  slidesToShow?: number;
  autoPlay?: boolean | { interval: number };
  gap?: number;
  slideCreep?: number;
  slideCreepLeft?: number;
  slideCreepRight?: number;
  showXGap?: boolean;
  slideWrapperStyles?: SerializedStyles;
  fade?: boolean;
  fadeStyles?: SerializedStyles;
  leftArrowStyles?: SerializedStyles;
  rightArrowStyles?: SerializedStyles;
  onSlideChange?: ({
    slides,
    middle,
  }: {
    slides?: any[];
    middle?: number;
  }) => void;
  currentSlide?: number;
  hideArrows?: boolean;
}> = ({
  slides = [],
  slidesToShow = 3,
  autoPlay = false,
  gap = 8,
  slideCreep = 0,
  slideCreepLeft,
  slideCreepRight,
  slideWrapperStyles,
  fade = false,
  fadeStyles,
  leftArrowStyles,
  rightArrowStyles,
  showXGap = true,
  onSlideChange,
  currentSlide,
  hideArrows,
  ...props
}) => {
  const { container, wrapper, slidesWrapper, fadeWrapper, arrow } = useStyle();
  const [paused, setPaused] = useState(false);
  const [offsetSlides, setOffsetSlides] = useState(slides);
  const [slideInfo, setSlides] = useState<[any[], number]>([
    slides,
    Math.floor(slidesToShow / 2),
  ]);
  const [carouselSlides, middle] = slideInfo;
  const [ref, bounds] = useMeasure({ debounce: 100 });
  const [scope, animate] = useAnimate();
  const creepLeft =
    typeof slideCreepLeft === 'undefined' ? slideCreep : slideCreepLeft;
  const creepRight =
    typeof slideCreepRight === 'undefined' ? slideCreep : slideCreepRight;
  const oneHundred = bounds.width;
  const oneHundredMinusGaps =
    oneHundred -
    creepLeft -
    creepRight -
    (showXGap ? slidesToShow + 1 : slidesToShow - 1) * gap;
  const slideWidth = oneHundredMinusGaps / slidesToShow;
  const creepAmount =
    typeof slideCreepLeft === 'undefined' &&
    typeof slideCreepRight === 'undefined'
      ? slideCreep
      : (slideCreepLeft || 0) + (slideCreepRight || 0);
  const totalSlideWidth = slideWidth - creepAmount;
  const slideWidthInPercent = (100 * slideWidth) / oneHundred;
  const offset = !showXGap ? gap : 0;
  const directionRef = useRef(1);
  const direction = directionRef.current;

  useEffect(() => {
    const getOffsetSlides = () => {
      const tempSlides = [...carouselSlides];
      const first = carouselSlides[0];
      const last = tempSlides.pop();
      tempSlides.unshift(last);
      if (slidesToShow === slides.length) {
        tempSlides.push(last);
        tempSlides.push(first);
      }
      return tempSlides;
    };

    const s = getOffsetSlides();
    setOffsetSlides(s);
  }, [carouselSlides]);

  const animateLeft = useCallback(
    async (count: number) => {
      await animate(
        scope.current,
        {
          x: [
            totalSlideWidth * (count - 1) - offset + gap,
            -1 * (totalSlideWidth + offset),
          ],
        },
        { type: 'spring', stiffness: 130, damping: 20, mass: 1 }
      );
    },
    [totalSlideWidth, offset, gap]
  );

  const animateRight = useCallback(
    async (count: number) => {
      await animate(
        scope.current,
        {
          x: [
            -2 * totalSlideWidth - totalSlideWidth * (count - 1) - offset - gap,
            -1 * (totalSlideWidth + offset),
          ],
        },
        { type: 'spring', stiffness: 130, damping: 20, mass: 1 }
      );
    },
    [totalSlideWidth, offset, gap]
  );

  const handleSlideChange = async (num: number) => {
    if (num === 0) {
      return;
    }
    directionRef.current = num;
    if (num > 0) {
      setSlides(([currSlides, currMid]) => {
        const temp = [...currSlides];
        for (let i = 0; i < num; i++) {
          temp.push(temp.shift());
        }

        const newMid =
          currMid + num >= currSlides.length
            ? 0 + num - (currSlides.length - currMid)
            : currMid + num;

        return [temp, newMid];
      });
    }
    if (num < 0) {
      setSlides(([currSlides, currMid]) => {
        const temp = [...currSlides];
        for (let i = 0; i < Math.abs(num); i++) {
          temp.unshift(temp.pop());
        }

        const newMid =
          currMid + num < 0 ? currSlides.length - 1 : currMid + num;

        return [temp, newMid];
      });
    }
  };

  useEffect(() => {
    let autoPlayInterval: ReturnType<typeof setInterval>;
    if (autoPlay && !paused) {
      const interval = typeof autoPlay === 'object' ? autoPlay.interval : 2500;
      autoPlayInterval = setInterval(() => {
        handleSlideChange(1);
      }, interval);
    }

    return () => clearTimeout(autoPlayInterval);
  }, [paused]);

  useEffect(() => {
    const diff = (currentSlide || 0) - middle;
    handleSlideChange(diff);
  }, [currentSlide]);

  useEffect(() => {
    if (direction > 0) {
      animateLeft(direction);
    }
    if (direction < 0) {
      animateRight(Math.abs(direction));
    }
  }, [carouselSlides]);

  useEffect(() => {
    setSlides([slides, Math.floor(slidesToShow / 2)]);
  }, [slidesToShow]);

  useEffect(() => {
    onSlideChange?.({ slides: carouselSlides, middle });
  }, [slideInfo]);

  const renderedSlides = useMemo(
    () =>
      offsetSlides.map((slide, i) => {
        return (
          <SlideCard
            key={`slide-${i}-${oneHundred}`}
            paginate={handleSlideChange}
            css={slideWrapperStyles}
            style={{
              position: 'relative',
              minWidth: `${slideWidthInPercent}%`,
              maxWidth: `${slideWidthInPercent}%`,
            }}
          >
            {slide}
          </SlideCard>
        );
      }),
    [offsetSlides, oneHundred, middle, slideWidthInPercent]
  );

  return (
    <div
      css={container}
      onMouseOver={() => {
        if (autoPlay) {
          setPaused(true);
        }
      }}
      onMouseOut={() => {
        if (autoPlay) {
          setPaused(false);
        }
      }}
    >
      <div ref={ref} css={wrapper} {...props}>
        <motion.div
          ref={scope}
          css={slidesWrapper}
          style={{ gap }}
          animate={{ x: -1 * (totalSlideWidth + offset) }}
          transition={{ duration: 0 }}
        >
          {renderedSlides}
        </motion.div>
        {fade && (
          <div
            css={css`
              ${fadeWrapper}
              ${fadeStyles}
            `}
          ></div>
        )}
        {!hideArrows && (
          <>
            <button
              css={css`
                ${arrow}
                ${leftArrowStyles}
              `}
              onClick={() => {
                handleSlideChange(-1);
              }}
            >
              <Arrow />
            </button>
            <button
              css={css`
                ${arrow}
                left: unset;
                right: 50px;
                ${rightArrowStyles}
              `}
              onClick={() => {
                handleSlideChange(1);
              }}
            >
              <Arrow style={{ rotate: '180deg' }} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default InfiniteSlideShow;

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

const SlideCard: React.FC<
  PropsWithChildren & {
    paginate: (arg0: number) => void;
    style: {};
    css?: SerializedStyles;
  }
> = ({ paginate, style, css, children, ...props }) => {
  return (
    <motion.div
      style={style}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0}
      dragMomentum={false}
      key={useId()}
      onDragEnd={(e, { offset, velocity }) => {
        const swipe = swipePower(offset.x, velocity.x);

        if (swipe < -swipeConfidenceThreshold) {
          paginate(1);
        } else if (swipe > swipeConfidenceThreshold) {
          paginate(-1);
        }
      }}
      css={css}
      {...props}
    >
      {children}
    </motion.div>
  );
};
